import React, { useState, useEffect } from "react";
import { Result, Spin } from "antd";
import { ButtonLink } from "../../misc/button/Button";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../../../config/config";

function TestPaymentResultPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, failure, pending
  const [statusDetails, setStatusDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  // Récupérer merchantReference depuis query params ou sessionStorage
  const merchantRefFromUrl = searchParams.get("ref");
  const merchantRefFromSession = sessionStorage.getItem("testPaymentRef");
  const merchantReference = merchantRefFromUrl || merchantRefFromSession;

  useEffect(() => {
    if (!merchantReference) {
      setStatus("failure");
      setStatusDetails({
        error: "Aucune référence de paiement trouvée",
      });
      return;
    }

    // Polling du statut
    const checkStatus = async () => {
      try {
        console.log(`Polling attempt ${pollCount + 1}/10`);

        const response = await axios.get(
          `${BACKEND_URL}/api/test-payment/check-status/${merchantReference}`,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const { hostedCheckoutStatus, paymentStatus, statusCode } = response.data;

        console.log("Status received:", response.data);

        setStatusDetails(response.data);

        // Statuts finaux (arrêter le polling)
        const finalStatuses = [
          "PAYMENT_CREATED",
          "CAPTURED",
          "REJECTED",
          "CANCELLED",
          "REJECTED_CAPTURE",
          "REFUNDED",
        ];

        // Déterminer le statut pour l'UI
        if (paymentStatus === 9 || statusCode === 9 || hostedCheckoutStatus === "PAYMENT_CREATED") {
          // 9 = CAPTURED (succès)
          setStatus("success");
          return; // Arrêter le polling
        } else if (
          finalStatuses.includes(hostedCheckoutStatus) ||
          (statusCode && statusCode !== 9 && finalStatuses.includes(paymentStatus))
        ) {
          // Statut final mais pas succès = échec
          setStatus("failure");
          return; // Arrêter le polling
        } else {
          // Statut intermédiaire (CREATED, REDIRECTED, PENDING_CAPTURE, etc.)
          setStatus("pending");

          // Continuer le polling si pas au maximum
          if (pollCount < 9) {
            setPollCount(pollCount + 1);
          } else {
            // Maximum de tentatives atteint
            setStatus("pending");
            console.log("Max polling attempts reached");
          }
        }
      } catch (error) {
        console.error("Erreur check status:", error);
        setStatus("failure");
        setStatusDetails({
          error: error.response?.data?.error || "Erreur lors de la vérification du statut",
        });
      }
    };

    // Premier check immédiat
    if (pollCount === 0) {
      checkStatus();
    }

    // Polling toutes les 2 secondes
    if (status === "loading" || status === "pending") {
      const timer = setTimeout(() => {
        if (pollCount < 10) {
          checkStatus();
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [merchantReference, pollCount, status]);

  const renderResult = () => {
    switch (status) {
      case "loading":
      case "pending":
        return (
          <div className="block-right">
            <div className="info-result-payment">
              <Result
                icon={<Spin size="large" />}
                title="Vérification du paiement en cours..."
                subTitle={
                  <>
                    <p>Merci de patienter pendant que nous vérifions votre paiement.</p>
                    {statusDetails && (
                      <div style={{ marginTop: "20px", textAlign: "left" }}>
                        <p>
                          <strong>Status Worldline:</strong> {statusDetails.hostedCheckoutStatus}
                        </p>
                        <p>
                          <strong>Status Code:</strong> {statusDetails.statusCode || "N/A"}
                        </p>
                        <p>
                          <strong>Tentative:</strong> {pollCount + 1}/10
                        </p>
                      </div>
                    )}
                  </>
                }
              />
            </div>
          </div>
        );

      case "success":
        return (
          <div className="block-right">
            <div className="info-result-payment">
              <Result
                status="success"
                title="Paiement test réussi !"
                subTitle={
                  <>
                    <p>Le paiement de test de 1€ a été capturé avec succès.</p>
                    {statusDetails && (
                      <div style={{ marginTop: "20px", textAlign: "left" }}>
                        <p>
                          <strong>Status Worldline:</strong> {statusDetails.hostedCheckoutStatus}
                        </p>
                        <p>
                          <strong>Payment Status:</strong> {statusDetails.paymentStatus}
                        </p>
                        <p>
                          <strong>Status Code:</strong> {statusDetails.statusCode}
                        </p>
                        <p style={{ marginTop: "15px", color: "#666", fontStyle: "italic" }}>
                          Consultez var/log/test_payment.log pour les détails complets
                        </p>
                      </div>
                    )}
                  </>
                }
              />
              <div className="button-container">
                <ButtonLink to="/test-payment" children="Nouveau test" />
                <ButtonLink to="/tableDeclaration" children="Retour au dashboard" />
              </div>
            </div>
          </div>
        );

      case "failure":
        return (
          <div className="block-right">
            <div className="info-result-payment">
              <Result
                status="error"
                title="Paiement test échoué"
                subTitle={
                  <>
                    <p>Le paiement de test n'a pas pu être complété.</p>
                    {statusDetails && (
                      <div style={{ marginTop: "20px", textAlign: "left" }}>
                        {statusDetails.error ? (
                          <p>
                            <strong>Erreur:</strong> {statusDetails.error}
                          </p>
                        ) : (
                          <>
                            <p>
                              <strong>Status Worldline:</strong> {statusDetails.hostedCheckoutStatus}
                            </p>
                            <p>
                              <strong>Payment Status:</strong> {statusDetails.paymentStatus || "N/A"}
                            </p>
                            <p>
                              <strong>Status Code:</strong> {statusDetails.statusCode || "N/A"}
                            </p>
                          </>
                        )}
                        <p style={{ marginTop: "15px", color: "#666", fontStyle: "italic" }}>
                          Consultez var/log/test_payment.log pour les détails complets
                        </p>
                      </div>
                    )}
                  </>
                }
              />
              <div className="button-container">
                <ButtonLink to="/test-payment" children="Réessayer" />
                <ButtonLink to="/tableDeclaration" children="Retour au dashboard" />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderResult();
}

export default TestPaymentResultPage;
